/**
 * CustomPluginRow.tsx — 8208-B
 *
 * Single row for a user-created (custom) plugin in the FeedbackTypeSelector dropdown.
 * Shows: edit icon + plugin name. On hover: grey background + trash/pencil action icons.
 *
 * Default state: edit icon on left, name fills flex-1 space.
 * Hover state: same layout + grey background + trash + pencil icons appear on right.
 */
import React, { useState } from "react";
import styled from "styled-components";
import SVG from "react-inlinesvg";
import classnames from "classnames";
import { FeedbackPlugin } from "../../../shared/feedbackCategories";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CustomPluginRowProps {
  plugin: FeedbackPlugin;
  isSelected?: boolean;
  onDelete: (pluginId: string) => void;
  onEdit: (pluginId: string) => void;
  onSelect?: (categoryKey: string) => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const RowContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 4px;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;

  &.custom-plugin-row--selected {
    box-shadow: inset 0 0 0 2px var(--primary-cta);
  }

  &.custom-plugin-row--hovered {
    background-color: var(--menu-color-hover);
  }

  .custom-plugin-actions {
    display: none;
    align-items: center;
    gap: 8px;
  }

  &.custom-plugin-row--hovered .custom-plugin-actions {
    display: flex;
  }
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--grey-0);

  svg,
  img {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: currentColor;
  }
`;

const NameWrapper = styled.div`
  flex: 1;
  padding-left: 8px;
`;

const PluginName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--grey-0);
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionButton = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--grey-0);

  svg,
  img {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: currentColor;
  }

  &:hover {
    opacity: 0.7;
  }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomPluginRow({
  plugin,
  isSelected,
  onDelete,
  onEdit,
  onSelect,
}: CustomPluginRowProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(plugin.id);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(plugin.id);
  };

  const handleClick = () => {
    onSelect?.(plugin.name);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect?.(plugin.name);
    }
  };

  return (
    <RowContainer
      className={classnames("custom-plugin-row", {
        "custom-plugin-row--selected": isSelected,
        "custom-plugin-row--hovered": isHovered,
      })}
      role="button"
      tabIndex={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <IconWrapper className="custom-plugin-icon-wrapper">
        <SVG src="/assets/edit.svg" aria-hidden="true" />
      </IconWrapper>

      <NameWrapper className="custom-plugin-name-wrapper">
        <PluginName className="custom-plugin-name">{plugin.name}</PluginName>
      </NameWrapper>

      <div className="custom-plugin-actions">
        <ActionButton
          className="custom-plugin-delete-btn"
          role="button"
          tabIndex={0}
          aria-label={`Delete ${plugin.name}`}
          onClick={handleDelete}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onDelete(plugin.id);
            }
          }}
        >
          <SVG src="/assets/delete.svg" aria-hidden="true" />
        </ActionButton>

        <ActionButton
          className="custom-plugin-edit-btn"
          role="button"
          tabIndex={0}
          aria-label={`Edit ${plugin.name}`}
          onClick={handleEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onEdit(plugin.id);
            }
          }}
        >
          <SVG src="/assets/pencil.svg" aria-hidden="true" />
        </ActionButton>
      </div>
    </RowContainer>
  );
}

export default CustomPluginRow;
